import React, { useState, useEffect } from 'react';
import { ArrowLeft, Wrench, ClipboardCheck, Calendar, User, Clock, DollarSign, Loader2, ChevronRight, X, Image, Package, CheckCircle, AlertTriangle } from 'lucide-react';

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Language translations
const translations = {
  en: {
    title: 'Service History',
    noHistory: 'No Service History',
    noHistoryDesc: 'No inspections or repairs have been logged for this equipment yet.',
    inspection: 'INSPECTION',
    repair: 'REPAIR',
    inspections: 'Inspections',
    repairs: 'Repairs',
    photos: 'Photos',
    description: 'Description',
    performedBy: 'Performed By',
    equipmentHours: 'Equipment Hours',
    currentHours: 'Current Hours',
    nextServiceAt: 'Next service at',
    hrs: 'hrs',
    inspectionChecklist: 'Inspection Checklist',
    loadingDetails: 'Loading inspection details...',
    noChecklistItems: 'No checklist items found',
    partsReplaced: 'Parts Replaced',
    laborAndCost: 'Labor & Cost',
    laborHours: 'Labor Hours',
    estimatedCost: 'Estimated Cost',
    notes: 'Notes',
    pass: 'PASS',
    fail: 'FAIL',
    na: 'N/A',
    changed: 'CHANGED',
    notChanged: 'NOT CHANGED',
    issue: 'ISSUE',
    pending: 'PENDING',
    resolvedIssue: 'RESOLVED ISSUE',
    resolved: 'RESOLVED',
    reportedBy: 'Reported By',
    fixedBy: 'Fixed By',
    fixDescription: 'Fix Description',
    originalIssue: 'Original Issue',
    severity: 'Severity',
    critical: 'Critical',
    moderate: 'Moderate',
    minor: 'Minor',
    reportedOn: 'Reported',
    resolvedOn: 'Resolved'
  },
  es: {
    title: 'Historial de Servicio',
    noHistory: 'Sin Historial de Servicio',
    noHistoryDesc: 'Aún no se han registrado inspecciones o reparaciones para este equipo.',
    inspection: 'INSPECCIÓN',
    repair: 'REPARACIÓN',
    inspections: 'Inspecciones',
    repairs: 'Reparaciones',
    photos: 'Fotos',
    description: 'Descripción',
    performedBy: 'Realizado Por',
    equipmentHours: 'Horas del Equipo',
    currentHours: 'Horas Actuales',
    nextServiceAt: 'Próximo servicio a las',
    hrs: 'hrs',
    inspectionChecklist: 'Lista de Verificación',
    loadingDetails: 'Cargando detalles de inspección...',
    noChecklistItems: 'No se encontraron elementos de lista',
    partsReplaced: 'Partes Reemplazadas',
    laborAndCost: 'Trabajo y Costo',
    laborHours: 'Horas de Trabajo',
    estimatedCost: 'Costo Estimado',
    notes: 'Notas',
    pass: 'PASA',
    fail: 'FALLA',
    na: 'N/A',
    changed: 'CAMBIADO',
    notChanged: 'SIN CAMBIO',
    issue: 'PROBLEMA',
    pending: 'PENDIENTE',
    resolvedIssue: 'PROBLEMA RESUELTO',
    resolved: 'RESUELTO',
    reportedBy: 'Reportado Por',
    fixedBy: 'Arreglado Por',
    fixDescription: 'Descripción de la Reparación',
    originalIssue: 'Problema Original',
    severity: 'Gravedad',
    critical: 'Crítico',
    moderate: 'Moderado',
    minor: 'Menor',
    reportedOn: 'Reportado',
    resolvedOn: 'Resuelto'
  }
};

interface ServiceRecord {
  record_type: 'inspection' | 'repair' | 'resolved_issue';
  record_id: number;
  service_date: string;
  current_hours?: number | null;
  next_service_hours?: number | null;
  notes: string | null;
  description: string;
  performed_by_first_name: string;
  performed_by_last_name: string;
  parts_replaced?: string | null;
  labor_hours?: number | null;
  cost?: number | null;
  photos?: string | null;
  // Resolved issue specific fields
  severity?: 'minor' | 'moderate' | 'critical' | null;
  fix_description?: string | null;
  reporter_first_name?: string | null;
  reporter_last_name?: string | null;
  reported_date?: string | null;
}

interface InspectionItem {
  item_id: number;
  item_name: string;
  item_category: string;
  status: 'pass' | 'fail' | 'na' | 'completed' | 'issue_found' | 'not_needed' | 'pending' | null;
  // 'service' items (filters, fluids) read Changed/Not Changed; 'check' items
  // read Pass/Fail — same wording as the shop kiosk's HistoryRecordOverlay.
  item_type?: 'service' | 'check' | string;
  notes: string | null;
  photo_url: string | null;
  requires_photo: boolean;
  requires_notes: boolean;
}

interface InspectionDetails {
  inspection_id: number;
  template_name: string;
  inspection_status: string;
  current_hours: number | null;
  next_service_hours: number | null;
  notes: string | null;
  items: InspectionItem[];
}

interface ServiceHistoryProps {
  equipment: {
    equipment_id: number;
    equipment_name: string;
    bradanna_equipment_number: string;
    equipment_type: string;
  };
  onBack: () => void;
}

const ServiceHistory: React.FC<ServiceHistoryProps> = ({ equipment, onBack }) => {
  const [records, setRecords] = useState<ServiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ServiceRecord | null>(null);
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
  const [inspectionDetails, setInspectionDetails] = useState<InspectionDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const t = translations[language];

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  useEffect(() => {
    loadServiceHistory();
  }, [equipment.equipment_id]);

  const loadServiceHistory = async () => {
    try {
      setLoading(true);
      const url = `${API_BASE_URL}/api/equipment/${equipment.equipment_id}/service-history`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRecords(data.data);
      } else {
        setError(data.error || 'Failed to load service history');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load service history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/New_York'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  const parsePhotos = (photosJson: string | null | undefined): string[] => {
    if (!photosJson) return [];
    try {
      const parsed = JSON.parse(photosJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const loadInspectionDetails = async (inspectionId: number) => {
    try {
      setLoadingDetails(true);
      const response = await fetch(`${API_BASE_URL}/api/equipment/inspection/${inspectionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setInspectionDetails(data.data);
      }
    } catch (err) {
      console.error('Error loading inspection details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRecordClick = (record: ServiceRecord) => {
    setSelectedRecord(record);
    setInspectionDetails(null);
    if (record.record_type === 'inspection') {
      loadInspectionDetails(record.record_id);
    }
  };

  const handleCloseModal = () => {
    setSelectedRecord(null);
    setInspectionDetails(null);
  };

  // Same wording as the shop kiosk (HistoryRecordOverlay): service items
  // (filters/fluids) read CHANGED / ISSUE / NOT CHANGED; checklist items read
  // PASS / FAIL / N/A.
  const getStatusColor = (status: string | null, itemType?: string) => {
    const service = itemType === 'service';
    switch (status) {
      case 'pass':
      case 'completed':
        return { bg: '#dcfce7', text: '#16a34a', label: service ? t.changed : t.pass };
      case 'fail':
      case 'issue_found':
        return { bg: '#fee2e2', text: '#dc2626', label: service ? t.issue : t.fail };
      case 'na':
      case 'not_needed':
        return { bg: '#f3f4f6', text: '#6b7280', label: service ? t.notChanged : t.na };
      case 'pending':
      default:
        return { bg: '#fef3c7', text: '#d97706', label: t.pending };
    }
  };

  const groupItemsByCategory = (items: InspectionItem[]) => {
    const groups: { [key: string]: InspectionItem[] } = {};
    items.forEach(item => {
      const category = item.item_category || 'General';
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
    });
    return groups;
  };

  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '400px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: '#3b82f6',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px',
      color: 'white'
    },
    headerTitle: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '6px'
    },
    headerSubtitle: {
      fontSize: '16px',
      opacity: 0.9
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '2px solid #ef4444',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px'
    },
    errorText: {
      color: '#991b1b',
      fontWeight: '500',
      fontSize: '16px'
    },
    emptyState: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: '40px',
      textAlign: 'center' as const
    },
    emptyIconCircle: {
      width: '80px',
      height: '80px',
      backgroundColor: '#f1f5f9',
      borderRadius: '50%',
      margin: '0 auto 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    emptyTitle: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px'
    },
    emptyText: {
      fontSize: '16px',
      color: '#64748b'
    },
    recordCard: (isInspection: boolean) => ({
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      overflow: 'hidden',
      marginBottom: '12px',
      borderLeft: `5px solid ${isInspection ? '#3b82f6' : '#ea580c'}`,
      cursor: 'pointer',
      transition: 'transform 0.15s, box-shadow 0.15s'
    }),
    recordButton: {
      width: '100%',
      padding: '18px',
      textAlign: 'left' as const,
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer'
    },
    recordContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    recordLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      flex: 1
    },
    recordIcon: (isInspection: boolean) => ({
      width: '50px',
      height: '50px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backgroundColor: isInspection ? '#dbeafe' : '#fed7aa'
    }),
    typeBadge: (isInspection: boolean) => ({
      display: 'inline-block',
      fontSize: '12px',
      fontWeight: '700',
      padding: '4px 10px',
      borderRadius: '6px',
      backgroundColor: isInspection ? '#3b82f6' : '#ea580c',
      color: 'white',
      marginBottom: '6px'
    }),
    recordDate: {
      fontSize: '15px',
      color: '#64748b',
      fontWeight: '500'
    },
    recordTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      marginTop: '4px',
      lineHeight: 1.3
    },
    recordMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '15px',
      color: '#64748b',
      marginTop: '6px'
    },
    summaryFooter: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: '20px',
      marginTop: '20px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px',
      textAlign: 'center' as const
    },
    summaryValue: {
      fontSize: '36px',
      fontWeight: '700',
      marginBottom: '4px'
    },
    summaryLabel: {
      fontSize: '16px',
      color: '#64748b',
      fontWeight: '500'
    },
    loadingContainer: {
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto'
    },
    loadingContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px'
    },
    // Modal styles
    modalBackdrop: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '16px',
      paddingTop: '5vh',
      paddingBottom: '40vh',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch' as const
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '85vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const,
      flexShrink: 0
    },
    modalHeader: (isInspection: boolean) => ({
      backgroundColor: isInspection ? '#3b82f6' : '#ea580c',
      padding: '20px',
      color: 'white',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    }),
    modalCloseBtn: {
      backgroundColor: 'rgba(255,255,255,0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalBody: {
      padding: '20px',
      overflowY: 'auto' as const,
      flex: 1
    },
    modalSection: {
      marginBottom: '20px'
    },
    modalSectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      marginBottom: '10px'
    },
    modalInfoCard: {
      backgroundColor: '#f8fafc',
      borderRadius: '12px',
      padding: '16px'
    },
    modalInfoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '14px'
    },
    modalInfoIcon: {
      width: '44px',
      height: '44px',
      borderRadius: '10px',
      backgroundColor: '#e2e8f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    modalInfoLabel: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '2px'
    },
    modalInfoValue: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1e293b'
    },
    modalDescription: {
      fontSize: '18px',
      lineHeight: 1.5,
      color: '#374151'
    },
    modalNotes: {
      fontSize: '17px',
      lineHeight: 1.6,
      color: '#475569',
      whiteSpace: 'pre-wrap' as const
    },
    photoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px'
    },
    photoThumb: {
      width: '100%',
      aspectRatio: '1',
      objectFit: 'cover' as const,
      borderRadius: '10px',
      cursor: 'pointer',
      border: '2px solid #e2e8f0'
    },
    // Photo viewer
    photoViewer: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      zIndex: 2000,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center'
    },
    photoViewerClose: {
      position: 'absolute' as const,
      top: '20px',
      right: '20px',
      backgroundColor: 'rgba(255,255,255,0.2)',
      border: 'none',
      borderRadius: '50%',
      width: '44px',
      height: '44px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    photoViewerImg: {
      maxWidth: '90%',
      maxHeight: '80vh',
      objectFit: 'contain' as const,
      borderRadius: '8px'
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <Loader2 size={40} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>{t.title}</h1>
        <p style={styles.headerSubtitle}>
          {equipment.equipment_name} (#{equipment.bradanna_equipment_number})
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div style={styles.errorBox}>
          <p style={styles.errorText}>{error}</p>
        </div>
      )}

      {/* Service Records List */}
      {records.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIconCircle}>
            <Calendar size={40} color="#94a3b8" />
          </div>
          <h2 style={styles.emptyTitle}>{t.noHistory}</h2>
          <p style={styles.emptyText}>
            {t.noHistoryDesc}
          </p>
        </div>
      ) : (
        <div>
          {records.map((record) => {
            const isInspection = record.record_type === 'inspection';
            const isRepair = record.record_type === 'repair';
            const isResolvedIssue = record.record_type === 'resolved_issue';
            const photos = parsePhotos(record.photos);

            // Get colors based on record type
            const getRecordColor = () => {
              if (isInspection) return '#3b82f6';
              if (isRepair) return '#ea580c';
              return '#22c55e'; // resolved issue - green
            };

            const getIconBgColor = () => {
              if (isInspection) return '#dbeafe';
              if (isRepair) return '#fed7aa';
              return '#dcfce7'; // resolved issue - light green
            };

            const getSeverityInfo = (severity: string | null | undefined) => {
              switch (severity) {
                case 'critical': return { color: '#dc2626', bg: '#fee2e2', label: t.critical };
                case 'moderate': return { color: '#f59e0b', bg: '#fef3c7', label: t.moderate };
                case 'minor': return { color: '#22c55e', bg: '#dcfce7', label: t.minor };
                default: return null;
              }
            };

            return (
              <div
                key={`${record.record_type}-${record.record_id}`}
                style={{
                  ...styles.recordCard(isInspection),
                  borderLeftColor: getRecordColor()
                }}
                onClick={() => handleRecordClick(record)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                }}
              >
                <div style={styles.recordButton}>
                  <div style={styles.recordContent}>
                    <div style={styles.recordLeft}>
                      <div style={{ ...styles.recordIcon(isInspection), backgroundColor: getIconBgColor() }}>
                        {isInspection ? (
                          <ClipboardCheck size={26} color="#2563eb" />
                        ) : isRepair ? (
                          <Wrench size={26} color="#ea580c" />
                        ) : (
                          <CheckCircle size={26} color="#22c55e" />
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <div style={{ ...styles.typeBadge(isInspection), backgroundColor: getRecordColor() }}>
                            {isInspection ? t.inspection : isRepair ? t.repair : t.resolvedIssue}
                          </div>
                          {/* Severity badge for repairs linked to issues or resolved issues */}
                          {(isRepair || isResolvedIssue) && record.severity && (() => {
                            const severityInfo = getSeverityInfo(record.severity);
                            if (!severityInfo) return null;
                            return (
                              <div style={{
                                display: 'inline-block',
                                fontSize: '11px',
                                fontWeight: '700',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                backgroundColor: severityInfo.bg,
                                color: severityInfo.color
                              }}>
                                {severityInfo.label}
                              </div>
                            );
                          })()}
                        </div>
                        <div style={styles.recordDate}>{formatDate(record.service_date)}</div>
                        <h3 style={styles.recordTitle}>{record.description}</h3>

                        {/* For repairs linked to issues or resolved issues, show reporter info */}
                        {(isRepair || isResolvedIssue) && record.reporter_first_name && (
                          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                            {t.reportedBy}: {record.reporter_first_name} {record.reporter_last_name}
                            {record.reported_date && ` • ${formatDate(record.reported_date)}`}
                          </div>
                        )}

                        <div style={styles.recordMeta}>
                          <User size={16} />
                          <span>
                            {record.performed_by_first_name || 'Unknown'} {record.performed_by_last_name || ''}
                          </span>
                          {record.current_hours != null && (
                            <>
                              <span style={{ margin: '0 4px' }}>•</span>
                              <Clock size={16} />
                              <span>{record.current_hours.toLocaleString()} {t.hrs}</span>
                            </>
                          )}
                          {photos.length > 0 && (
                            <>
                              <span style={{ margin: '0 4px' }}>•</span>
                              <Image size={16} />
                              <span>{photos.length} {t.photos.toLowerCase()}</span>
                            </>
                          )}
                        </div>

                        {/* Photo thumbnails for repairs and resolved issues */}
                        {(isRepair || isResolvedIssue) && photos.length > 0 && (
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            marginTop: '12px',
                            flexWrap: 'wrap'
                          }}>
                            {photos.slice(0, 3).map((photo, idx) => (
                              <img
                                key={idx}
                                src={photo}
                                alt={`Photo ${idx + 1}`}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  border: '2px solid #e2e8f0'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingPhoto(photo);
                                }}
                              />
                            ))}
                            {photos.length > 3 && (
                              <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '8px',
                                backgroundColor: '#f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#64748b'
                              }}>
                                +{photos.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <ChevronRight size={24} color="#9ca3af" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Footer */}
      {records.length > 0 && (
        <div style={styles.summaryFooter}>
          <div style={styles.summaryGrid}>
            <div>
              <div style={{ ...styles.summaryValue, color: '#3b82f6' }}>
                {records.filter((r) => r.record_type === 'inspection').length}
              </div>
              <div style={styles.summaryLabel}>{t.inspections}</div>
            </div>
            <div>
              <div style={{ ...styles.summaryValue, color: '#ea580c' }}>
                {records.filter((r) => r.record_type === 'repair' || r.record_type === 'resolved_issue').length}
              </div>
              <div style={styles.summaryLabel}>{t.repairs}</div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRecord && (
        <div style={styles.modalBackdrop} onClick={handleCloseModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              ...styles.modalHeader(selectedRecord.record_type === 'inspection'),
              backgroundColor: selectedRecord.record_type === 'inspection' ? '#3b82f6' :
                             selectedRecord.record_type === 'repair' ? '#ea580c' : '#22c55e'
            }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', opacity: 0.9, marginBottom: '4px' }}>
                  {selectedRecord.record_type === 'inspection' ? t.inspection :
                   selectedRecord.record_type === 'repair' ? t.repair : t.resolvedIssue}
                </div>
                <div style={{ fontSize: '22px', fontWeight: '700' }}>
                  {formatDate(selectedRecord.service_date)}
                </div>
                <div style={{ fontSize: '16px', opacity: 0.9, marginTop: '4px' }}>
                  {formatTime(selectedRecord.service_date)}
                </div>
              </div>
              <button style={styles.modalCloseBtn} onClick={handleCloseModal}>
                <X size={24} color="white" />
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {/* Severity Badge for Resolved Issues */}
              {selectedRecord.record_type === 'resolved_issue' && selectedRecord.severity && (
                <div style={{
                  display: 'inline-block',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  backgroundColor: selectedRecord.severity === 'critical' ? '#fee2e2' :
                                   selectedRecord.severity === 'moderate' ? '#fef3c7' : '#dcfce7',
                  color: selectedRecord.severity === 'critical' ? '#dc2626' :
                         selectedRecord.severity === 'moderate' ? '#d97706' : '#16a34a'
                }}>
                  {selectedRecord.severity === 'critical' ? t.critical :
                   selectedRecord.severity === 'moderate' ? t.moderate : t.minor}
                </div>
              )}

              {/* Description / Original Issue */}
              <div style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>
                  {selectedRecord.record_type === 'resolved_issue' ? t.originalIssue : t.description}
                </div>
                <p style={styles.modalDescription}>{selectedRecord.description}</p>
              </div>

              {/* Reporter Info for Resolved Issues */}
              {selectedRecord.record_type === 'resolved_issue' && selectedRecord.reporter_first_name && (
                <div style={styles.modalSection}>
                  <div style={styles.modalSectionTitle}>{t.reportedBy}</div>
                  <div style={styles.modalInfoCard}>
                    <div style={{ ...styles.modalInfoRow, marginBottom: 0 }}>
                      <div style={{ ...styles.modalInfoIcon, backgroundColor: '#fef3c7' }}>
                        <AlertTriangle size={22} color="#d97706" />
                      </div>
                      <div>
                        <div style={styles.modalInfoValue}>
                          {selectedRecord.reporter_first_name} {selectedRecord.reporter_last_name}
                        </div>
                        {selectedRecord.reported_date && (
                          <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>
                            {t.reportedOn}: {formatDate(selectedRecord.reported_date)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fix Description for Resolved Issues */}
              {selectedRecord.record_type === 'resolved_issue' && selectedRecord.fix_description && (
                <div style={styles.modalSection}>
                  <div style={styles.modalSectionTitle}>{t.fixDescription}</div>
                  <div style={{ ...styles.modalInfoCard, backgroundColor: '#dcfce7' }}>
                    <p style={{ ...styles.modalNotes, color: '#166534' }}>{selectedRecord.fix_description}</p>
                  </div>
                </div>
              )}

              {/* Performed By / Fixed By */}
              <div style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>
                  {selectedRecord.record_type === 'resolved_issue' ? t.fixedBy : t.performedBy}
                </div>
                <div style={styles.modalInfoCard}>
                  <div style={{ ...styles.modalInfoRow, marginBottom: 0 }}>
                    <div style={{
                      ...styles.modalInfoIcon,
                      backgroundColor: selectedRecord.record_type === 'resolved_issue' ? '#dcfce7' : '#e2e8f0'
                    }}>
                      {selectedRecord.record_type === 'resolved_issue' ? (
                        <CheckCircle size={22} color="#22c55e" />
                      ) : (
                        <User size={22} color="#64748b" />
                      )}
                    </div>
                    <div>
                      <div style={styles.modalInfoValue}>
                        {selectedRecord.performed_by_first_name || 'Unknown'} {selectedRecord.performed_by_last_name || ''}
                      </div>
                      {selectedRecord.record_type === 'resolved_issue' && (
                        <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>
                          {t.resolvedOn}: {formatDate(selectedRecord.service_date)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Equipment hours (inspections record them manually; repairs/resolved issues snapshot GPS hours) */}
              {selectedRecord.current_hours != null && (
                <div style={styles.modalSection}>
                  <div style={styles.modalSectionTitle}>{t.equipmentHours}</div>
                  <div style={styles.modalInfoCard}>
                    <div style={{ ...styles.modalInfoRow, marginBottom: 0 }}>
                      <div style={{ ...styles.modalInfoIcon, backgroundColor: '#dbeafe' }}>
                        <Clock size={22} color="#3b82f6" />
                      </div>
                      <div>
                        <div style={styles.modalInfoLabel}>{t.currentHours}</div>
                        <div style={{ ...styles.modalInfoValue, color: '#3b82f6' }}>
                          {selectedRecord.current_hours.toLocaleString()} {t.hrs}
                        </div>
                        {selectedRecord.next_service_hours && (
                          <div style={{ fontSize: '15px', color: '#64748b', marginTop: '4px' }}>
                            {t.nextServiceAt} {selectedRecord.next_service_hours.toLocaleString()} {t.hrs}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Inspection Checklist Items */}
              {selectedRecord.record_type === 'inspection' && (
                <div style={styles.modalSection}>
                  <div style={styles.modalSectionTitle}>{t.inspectionChecklist}</div>
                  {loadingDetails ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Loader2 size={24} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
                      <div style={{ marginTop: '8px', color: '#64748b', fontSize: '15px' }}>{t.loadingDetails}</div>
                    </div>
                  ) : inspectionDetails?.items && inspectionDetails.items.length > 0 ? (
                    <div>
                      {Object.entries(groupItemsByCategory(inspectionDetails.items)).map(([category, items]) => (
                        <div key={category} style={{ marginBottom: '16px' }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#3b82f6',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '8px',
                            paddingBottom: '6px',
                            borderBottom: '2px solid #e2e8f0'
                          }}>
                            {category}
                          </div>
                          {items.map((item, idx) => {
                            const statusInfo = getStatusColor(item.status, item.item_type);
                            return (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                padding: '12px',
                                backgroundColor: statusInfo.bg,
                                borderRadius: '8px',
                                marginBottom: '8px'
                              }}>
                                <div style={{
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: statusInfo.text,
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  flexShrink: 0
                                }}>
                                  {statusInfo.label}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                                    {item.item_name}
                                  </div>
                                  {item.notes && (
                                    <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                                      {item.notes}
                                    </div>
                                  )}
                                  {item.photo_url && (
                                    <img
                                      src={item.photo_url}
                                      alt={item.item_name}
                                      style={{
                                        width: '80px',
                                        height: '80px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        marginTop: '8px',
                                        cursor: 'pointer',
                                        border: '2px solid #e2e8f0'
                                      }}
                                      onClick={() => setViewingPhoto(item.photo_url!)}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '16px', color: '#64748b', fontSize: '15px' }}>
                      {t.noChecklistItems}
                    </div>
                  )}
                </div>
              )}

              {/* Repair Details */}
              {(selectedRecord.record_type === 'repair' || selectedRecord.record_type === 'resolved_issue') && (
                <>
                  {/* Parts Replaced */}
                  {selectedRecord.parts_replaced && (
                    <div style={styles.modalSection}>
                      <div style={styles.modalSectionTitle}>{t.partsReplaced}</div>
                      <div style={{ ...styles.modalInfoCard, backgroundColor: '#fef3c7' }}>
                        <div style={{ ...styles.modalInfoRow, marginBottom: 0 }}>
                          <div style={{ ...styles.modalInfoIcon, backgroundColor: '#fde68a' }}>
                            <Package size={22} color="#d97706" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '17px', color: '#92400e', lineHeight: 1.5 }}>
                              {selectedRecord.parts_replaced}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Labor & Cost */}
                  {(selectedRecord.labor_hours || selectedRecord.cost) && (
                    <div style={styles.modalSection}>
                      <div style={styles.modalSectionTitle}>{t.laborAndCost}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: selectedRecord.labor_hours && selectedRecord.cost ? '1fr 1fr' : '1fr', gap: '12px' }}>
                        {selectedRecord.labor_hours && (
                          <div style={styles.modalInfoCard}>
                            <div style={{ textAlign: 'center' }}>
                              <Clock size={28} color="#64748b" style={{ marginBottom: '8px' }} />
                              <div style={styles.modalInfoLabel}>{t.laborHours}</div>
                              <div style={styles.modalInfoValue}>{selectedRecord.labor_hours} {t.hrs}</div>
                            </div>
                          </div>
                        )}
                        {selectedRecord.cost && (
                          <div style={styles.modalInfoCard}>
                            <div style={{ textAlign: 'center' }}>
                              <DollarSign size={28} color="#64748b" style={{ marginBottom: '8px' }} />
                              <div style={styles.modalInfoLabel}>{t.estimatedCost}</div>
                              <div style={styles.modalInfoValue}>${selectedRecord.cost.toFixed(2)}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Notes */}
              {selectedRecord.notes && (
                <div style={styles.modalSection}>
                  <div style={styles.modalSectionTitle}>{t.notes}</div>
                  <div style={styles.modalInfoCard}>
                    <p style={styles.modalNotes}>{selectedRecord.notes}</p>
                  </div>
                </div>
              )}

              {/* Photos */}
              {(() => {
                const photos = parsePhotos(selectedRecord.photos);
                if (photos.length === 0) return null;
                return (
                  <div style={styles.modalSection}>
                    <div style={styles.modalSectionTitle}>{t.photos} ({photos.length})</div>
                    <div style={styles.photoGrid}>
                      {photos.map((photo, index) => (
                        <img
                          key={index}
                          src={photo}
                          alt={`Service photo ${index + 1}`}
                          style={styles.photoThumb}
                          onClick={() => setViewingPhoto(photo)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Photo Viewer */}
      {viewingPhoto && (
        <div style={styles.photoViewer} onClick={() => setViewingPhoto(null)}>
          <button style={styles.photoViewerClose} onClick={() => setViewingPhoto(null)}>
            <X size={24} color="white" />
          </button>
          <img
            src={viewingPhoto}
            alt="Full size"
            style={styles.photoViewerImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ServiceHistory;
